import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, useMe } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import { SignInForm } from '../accounts/LoginPage'

type Grant = {
  user_code: string; client_name: string; ip: string | null; requested_at: string
  status: 'pending' | 'approved' | 'denied' | 'expired'
}

const CAN = ['Do what you can do in ernd', 'See only what you can see']
const CANNOT = ['Change bank details', 'Approve payouts', 'Make or approve other tokens']

/** /agent/connect: the person types the code their agent showed them, then approves it (pr/agentauth). A code in the link
 * would let anyone send a victim straight to Approve, so this page never reads one (RFC 8628 section 5.4). */
export function AgentConnectPage() {
  const [typed, setTyped] = useState('')
  const [code, setCode] = useState('')
  const [done, setDone] = useState<'approved' | 'denied' | null>(null)
  const [staff, setStaff] = useState(false)
  // Once approved, watch for the company the agent creates so this page can link straight to it.
  const { me, isLoading } = useMe(done === 'approved' ? 3000 : undefined)

  const grant = useQuery({
    queryKey: ['agent', 'device', code],
    queryFn: () => api<Grant>('GET', `/api/agent/device/${encodeURIComponent(code)}`),
    enabled: !!me?.user && !!code,
    retry: false,
  })
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api<{ status: string }>('POST', `/api/agent/device/${encodeURIComponent(code)}/${approve ? 'approve' : 'deny'}`, approve ? { staff } : undefined),
    onSuccess: (r) => setDone(r.status === 'approved' ? 'approved' : 'denied'),
  })

  if (isLoading) return <Loading />
  if (!me?.user) {
    return (
      <div className="page narrow stack" data-testid="agent-connect-signin">
        <div className="stack" style={{ gap: '0.5rem' }}>
          <h1>Connect your agent</h1>
          <p className="muted" style={{ margin: 0 }}>Sign in, then type the code your agent showed you. New here? Signing in creates your account.</p>
        </div>
        <SignInForm next="/agent/connect" />
      </div>
    )
  }
  if (code && grant.isLoading) return <Loading />
  if (!code || grant.error) {
    return (
      <form
        className="page narrow stack"
        data-testid="agent-connect-code"
        onSubmit={(e) => { e.preventDefault(); setCode(typed.trim().toUpperCase()) }}
      >
        <h1>Connect your agent</h1>
        <p className="muted">Type the code your agent showed you. If you didn’t start this, close this page.</p>
        {grant.error ? <ErrorBox error={grant.error} /> : null}
        <input
          className="e-input e-input-lg"
          aria-label="Code"
          autoComplete="off"
          placeholder="XXXX-XXXX"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          data-testid="agent-code"
        />
        <div className="row">
          <button className="primary" type="submit" disabled={!typed.trim()} data-testid="agent-code-go">Continue</button>
        </div>
      </form>
    )
  }
  const g = grant.data!

  if (done || g.status !== 'pending') {
    const status = done ?? g.status
    const company = me.companies[0]
    return (
      <div className="page narrow stack" data-testid="agent-connect-done">
        <h1>{status === 'approved' ? 'You’re connected' : status === 'denied' ? 'Not connected' : 'This code expired'}</h1>
        <p className="muted">
          {status === 'approved'
            ? company
              ? `${g.client_name} is connected. Go back to it.`
              : `Go back to ${g.client_name}.`
            : status === 'denied'
              ? `${g.client_name} can’t act for you. You can close this page.`
              : 'Ask your agent for a new code.'}
        </p>
        {status === 'approved' && company ? <Link className="button primary" to="/" data-testid="agent-connect-open">Open {company.name}</Link> : null}
      </div>
    )
  }

  return (
    <div className="page narrow stack" data-testid="agent-connect">
      <div className="stack" style={{ gap: '0.5rem' }}>
        <p className="muted mono">{g.user_code}</p>
        <h1>An agent wants to work in ernd as you</h1>
        <p className="muted" data-testid="agent-asked">
          It calls itself “{g.client_name}”, a name it chose. It asked {new Date(g.requested_at).toLocaleString()}{g.ip ? ` from ${g.ip}` : ''}.
        </p>
        <p className="muted">If you didn’t start this, press Deny. Signed in as {me.user.email}.</p>
      </div>
      <div className="slip stack" style={{ gap: '0.75rem' }}>
        <div className="muted" style={{ fontSize: '0.8125rem' }}>It can</div>
        <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>{CAN.map((c) => <li key={c}>{c}</li>)}</ul>
        <div className="muted" style={{ fontSize: '0.8125rem', paddingTop: '0.5rem' }}>It can’t</div>
        <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>{CANNOT.map((c) => <li key={c}>{c}</li>)}</ul>
      </div>
      {me.is_staff ? (
        <label className="row" style={{ gap: '0.5rem' }}>
          <input type="checkbox" checked={staff} onChange={(e) => setStaff(e.target.checked)} data-testid="agent-staff" />
          Also give it staff access
        </label>
      ) : null}
      {decide.error ? <ErrorBox error={decide.error} /> : null}
      <div className="row">
        <button className="primary" type="button" disabled={decide.isPending} onClick={() => decide.mutate(true)} data-testid="agent-approve">
          Approve
        </button>
        <button type="button" disabled={decide.isPending} onClick={() => decide.mutate(false)} data-testid="agent-deny">
          Deny
        </button>
      </div>
    </div>
  )
}
